/** Subset of a reference-PDF open request relevant to viewer placement. */
export interface ReferencePdfOpenTarget {
  page?: number;
  query?: string;
  highlight?: string;
}

export interface ReferencePdfOpenState {
  /** Page to open at when known synchronously; null when a search must resolve it first. */
  page: number | null;
  /** Query to run in the viewer's search bar (unmapped Table/Figure refs). */
  query?: string;
  /** Display-only search-bar text for mapped refs (so the user sees e.g. "Table 16-3"). */
  searchText?: string;
  /** Terms to highlight on the landed page without running a search. */
  highlightTerms?: string[];
  /** True when `page` is null and a BM25 search is needed to resolve the page. */
  needsSearch: boolean;
}

/**
 * Map a reference-PDF open request (chatbot link, IR result-list link, Learn
 * More button) to the `ReferencePdfFloatingViewer` open state:
 *   - scheme link `{ page }`                  -> jump to page, no search
 *   - mapped Table/Figure `{ page, highlight }` -> jump + seed highlight/search text
 *   - unmapped Table/Figure `{ query, highlight }` -> search to resolve the page
 */
export function deriveReferencePdfOpenState(
  target: ReferencePdfOpenTarget,
): ReferencePdfOpenState {
  const needsSearch = target.page === undefined && !!target.query;
  return {
    page: target.page ?? (needsSearch ? null : 1),
    query: target.query,
    // When a query runs, the search bar seeds itself from the query; only seed
    // the display text from `highlight` when there's no query (mapped jump).
    searchText: target.query ? undefined : target.highlight,
    highlightTerms: target.highlight ? [target.highlight] : undefined,
    needsSearch,
  };
}
